import React, { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'react-intl';
import { EditorialModule_editorialArticles } from './__generated__/EditorialModule_editorialArticles.graphql';

import styles from './styles/EditorialModule.scss';

const WORDPRESS_THUMB = 95;
const WORDPRESS_MEDIUM = 276;
const WORDPRESS_FULL = 768;
const getHeaderId = (index: number): string => `wp-article-header-${index}`;
const getDescriptionId = (index: number): string => `wp-article-description-${index}`;

function fireTracking({ source, title }: { source: string; title: string }): void {
    trackEvent({
        category: 'ecommerce',
        action: 'promotion click',
        label: `editorial article: ${source} ${title}`,
    });
}

type Props = {
    editorialArticles: EditorialModule_editorialArticles;
};

const EditorialModuleComponent: FC<Props> = ({ editorialArticles }) => {
    const articles = editorialArticles?.articles || [];
    if (!articles.length) {
        return null;
    }
    return (
        <HeadingLevel>
            {Heading => (
                <div className={styles.container}>
                    <Heading className={styles.sectionHeader}>
                        <FormattedMessage
                            defaultMessage="Read More"
                            id="dbl.editorialModule.header"
                        />
                    </Heading>
                    <div className={styles.articlesWrapper}>
                        {articles.map((article, index) => (
                            <HeadingLevel key={`wp-article-container-${index}`}>
                                {() => (
                                    <Link
                                        ariaLabel={`Read more about ${article.title}`}
                                        ariaDescribedBy={`${getHeaderId(index)},${getDescriptionId(
                                            index
                                        )}`}
                                        buyerLinkType="standardLinkNoEffect"
                                        underline="none"
                                        href={article.link}
                                        target="_blank"
                                        dataTn={`wp-article-link-${index}`}
                                        className={styles.article}
                                        onClick={() => fireTracking(article)}
                                    >
                                        <div className={styles.articleElement}>
                                            <img
                                                className={styles.featuredImg}
                                                src={article.imageUrl.thumb}
                                                srcSet={`
                                                    ${article.imageUrl.thumb} ${WORDPRESS_THUMB}w,
                                                    ${article.imageUrl.medium} ${WORDPRESS_MEDIUM}w,
                                                    ${article.imageUrl.full} ${WORDPRESS_FULL}w
                                                `}
                                                sizes="(max-width: 320px) 100vw, 25vw"
                                                alt={article.title}
                                            />
                                        </div>
                                        <div className={styles.articleElement}>
                                            <Heading
                                                className={styles.header}
                                                id={getHeaderId(index)}
                                                dangerouslySetInnerHTML={{ __html: article.title }}
                                            />
                                            <p
                                                className={styles.body}
                                                id={getDescriptionId(index)}
                                                dangerouslySetInnerHTML={{
                                                    __html: article.subtitle,
                                                }}
                                            />
                                        </div>
                                    </Link>
                                )}
                            </HeadingLevel>
                        ))}
                    </div>
                </div>
            )}
        </HeadingLevel>
    );
};

export const EditorialModule = createFragmentContainer(EditorialModuleComponent, {
    editorialArticles: graphql`
        fragment EditorialModule_editorialArticles on WordpressArticlesType {
            articles {
                title
                imageUrl {
                    thumb
                    medium
                    full
                }
                subtitle
                publishedDate
                link
                source
            }
        }
    `,
});
